import { useQuery } from '@tanstack/react-query';
import patientService from '../services/patientService';
import carerService from '../services/carerService';

/**
 * Custom hook to fetch patient or carer details using React Query
 * @param {string} providerId - The provider ID
 * @param {string} patientId - The patient ID
 * @param {string} [carerId] - Optional carer ID. If provided, fetches carer details instead
 * @param {Object} [options] - Additional React Query options
 * @returns {Object} React Query result object with data, isLoading, error, etc.
 */
export const usePatientDetails = (
  providerId,
  patientId,
  carerId = null,
  options = {},
) => {
  return useQuery({
    // Unique query key - includes carerId if fetching a carer
    queryKey: carerId
      ? ['carer', providerId, patientId, carerId]
      : ['patient', providerId, patientId],

    // Query function - fetches patient or carer based on carerId
    queryFn: async ({ meta }) => {
      // Support custom headers (e.g., for cache busting)
      const customHeaders = meta?.customHeaders || {};

      const response = carerId
        ? await carerService.getCarer(
            providerId,
            patientId,
            carerId,
            customHeaders,
          )
        : await patientService.getPatient(
            providerId,
            patientId,
            customHeaders,
          );

      // Handle errors from the API
      if (
        response === null ||
        response.data?.id === null ||
        response.data?.errors
      ) {
        const error = new Error('Failed to fetch details');
        error.status = response?.status || 500;
        error.response = response;
        throw error;
      }

      // Clean up UPDATENAME placeholders for carers
      if (carerId && response.data) {
        if (response.data.firstName === 'UPDATENAME') {
          response.data.firstName = '';
        }
        if (response.data.lastName === 'UPDATENAME') {
          response.data.lastName = '';
        }
      }

      return response.data;
    },

    // Only run the query if we have the required IDs
    enabled: Boolean(providerId && patientId),

    // Merge any additional options passed by the caller
    ...options,
  });
};
